import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HardDrive, Loader2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { usersAPI } from '../services/api';

// Phase C — Profile card that lets a signed-in user turn on their own NAS
// Game Library. Admin still owns resize/disable (see src/pages/Admin.js).
//
// Phase E — Family seats 1..3 see the master's household storage status
// read-only (``is_household_member=true``). The master seat (0) and solo
// users keep the self-service enable flow.

const READY_STATES = new Set(['ready', 'ready_no_format']);

const prettyStatus = (status) => {
  if (!status) return 'Not enabled';
  if (status === 'queued') return 'Queued for provisioning';
  if (status === 'provisioning') return 'Creating LUN on NAS';
  if (status === 'formatting') return 'Preparing filesystem';
  if (status === 'ready') return 'Ready — formatted';
  if (status === 'ready_no_format') return 'Ready — formats on first attach';
  if (status === 'failed') return 'Failed';
  if (status === 'disabled') return 'Disabled';
  return status;
};

const NasStorageCard = () => {
  const queryClient = useQueryClient();
  const [chosenSize, setChosenSize] = useState(null);

  const { data, isLoading } = useQuery(
    'myStorage',
    usersAPI.getMyStorage,
    {
      // Poll fast while a job is in flight, slow once ready.
      refetchInterval: (query) => {
        const row = query?.state?.data;
        if (!row || !row.enabled) return false;
        if (READY_STATES.has(row.status)) return 60000;
        return 4000;
      },
      staleTime: 2000,
    },
  );

  const remove = useMutation(() => usersAPI.removeMyStorage(), {
    onSuccess: (payload) => {
      toast.success('NAS Storage removal started');
      queryClient.setQueryData('myStorage', payload);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Could not remove NAS Storage');
    },
  });

  const enable = useMutation(
    (sizeGb) => usersAPI.enableMyStorage(sizeGb),
    {
      onSuccess: (payload) => {
        toast.success(`NAS Storage queued at ${payload.size_gb} GB`);
        queryClient.setQueryData('myStorage', payload);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          'Could not enable NAS Storage';
        toast.error(msg);
      },
    },
  );

  if (isLoading) {
    return (
      <div className="glass p-5 rounded-xl flex items-center gap-3 text-white/60">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading NAS Storage…
      </div>
    );
  }

  if (!data) return null;

  const size = chosenSize || data.recommended_size;
  const isReady = READY_STATES.has(data.status);
  const isHouseholdMember = Boolean(data.is_household_member);

  return (
    <div className="glass p-6 rounded-xl">
      <div className="flex items-center gap-3 mb-2">
        {isHouseholdMember ? (
          <Users className="w-5 h-5 text-fuchsia-300" />
        ) : (
          <HardDrive className="w-5 h-5 text-sky-300" />
        )}
        <h3 className="text-white font-semibold">
          {isHouseholdMember ? 'Household NAS Game Library' : 'NAS Game Library'}
        </h3>
      </div>
      {isHouseholdMember ? (
        <p className="text-white/60 text-sm mb-4">
          Shared household library owned by{' '}
          <span className="text-white font-medium">
            {data.household_admin_username || 'the family master'}
          </span>
          . Every seat on your family plan sees the same installed games
          — one Steam install re-hydrates for all of you.
        </p>
      ) : (
        <p className="text-white/60 text-sm mb-4">
          Keeps your installed games on the NAS so every GPU session starts
          with your library already there — no re-downloads, no reinstalls.
        </p>
      )}

      {data.enabled ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-sm">Status</span>
            <span
              className={
                'text-xs px-2 py-0.5 rounded ' +
                (isReady
                  ? 'bg-emerald-500/20 text-emerald-200'
                  : data.status === 'failed'
                  ? 'bg-red-500/20 text-red-200'
                  : 'bg-sky-500/20 text-sky-200')
              }
            >
              {prettyStatus(data.status)}
            </span>
          </div>
          {data.size_gb ? (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">Size</span>
              <span className="text-white">{data.size_gb} GB</span>
            </div>
          ) : null}
          <div>
            <div className="h-2 rounded bg-white/10 overflow-hidden">
              <div
                className={
                  'h-full ' + (isReady ? 'bg-emerald-400' : 'bg-sky-400')
                }
                style={{
                  width: `${Math.max(
                    0,
                    Math.min(100, data.progress_pct || 0),
                  )}%`,
                }}
              />
            </div>
            <div className="text-xs text-white/50 mt-1">
              {data.progress_label ||
                (isReady ? 'Ready for your next session' : 'Working on it…')}
            </div>
          </div>
          {data.in_grace && data.purge_after ? (
            <p className="text-xs text-amber-200">
              Subscription ended. This library stays until{' '}
              {new Date(data.purge_after).toLocaleDateString()} and is then
              removed.
            </p>
          ) : null}
          {data.last_error ? (
            <div className="text-xs text-red-300">{data.last_error}</div>
          ) : null}
          {data.can_remove ? (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    'Remove NAS Storage? Installed games on this library will be deleted.',
                  )
                ) {
                  remove.mutate();
                }
              }}
              disabled={remove.isLoading}
              className="text-xs text-red-300 hover:text-red-200 disabled:opacity-50"
            >
              {remove.isLoading ? 'Removing…' : 'Remove NAS Storage'}
            </button>
          ) : null}
          {isReady ? (
            <p className="text-xs text-white/50">
              First attach to a GPU runs a quick ext4 format in the
              background — you can start playing right away, library sync
              catches up as it goes.
            </p>
          ) : null}
        </div>
      ) : isHouseholdMember ? (
        // Phase E — family member, master hasn't enabled yet.
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/70">Plan</span>
            <span className="text-white capitalize">{data.plan}</span>
          </div>
          <div className="rounded-md bg-fuchsia-500/10 border border-fuchsia-400/20 p-3 text-sm text-fuchsia-100">
            Not enabled yet. Ask{' '}
            <span className="font-medium">
              {data.household_admin_username || 'your family master'}
            </span>{' '}
            to turn on NAS Storage from their Profile — it covers every
            seat on the plan automatically.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/70">Plan</span>
            <span className="text-white capitalize">{data.plan}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-sm">Choose size</span>
            <select
              value={size}
              onChange={(e) => setChosenSize(Number(e.target.value))}
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm"
            >
              {(data.available_sizes || []).map((n) => (
                <option key={n} value={n}>
                  {n >= 1000 ? `${n / 1000} TB` : `${n} GB`}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => enable.mutate(size)}
            disabled={enable.isLoading || !data.can_enable}
            className="w-full px-4 py-2 rounded bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-sm font-medium"
          >
            {enable.isLoading ? 'Requesting…' : `Enable NAS Storage (${size} GB)`}
          </button>
          <p className="text-xs text-white/40">
            {data.plan === 'family'
              ? 'Family plan: one LUN covers all four seats. Need more? An admin can bump the size any time.'
              : 'Need more? An admin can bump the size any time from the platform admin panel.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default NasStorageCard;
